import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { getLongDate, getStringDate } from 'src/app/common/util';
import { ButtonLoading } from 'src/app/models/button-loading.model';
import { AssociationExitNoteSaleResponse, ExitNote } from 'src/app/models/exit-note.model';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { AssociationExitNoteService } from 'src/app/services/association-exit-note.service';
import { ExitNoteService } from 'src/app/services/exit-note.service';
import { InvoiceSaleService } from '../../../sales/invoice-sale/invoice-sale.service';
import { SalesService } from 'src/app/services/sales.service';

type SaleVM = {
  id: number;
  number: string;
  clientName: string;
  date: number | string;
  total: number;
  association: AssociationExitNoteSaleResponse;
};

type ExitNoteVM = {
  id: number;
  number: string;
  clientName: string;
  date: string;
};

@Component({
  selector: 'app-association-exit-note-sale',
  templateUrl: './association-exit-note-sale.component.html',
  styleUrls: ['./association-exit-note-sale.component.scss']
})
export class AssociationExitNoteSaleComponent implements OnInit {

  loading = false;

  allSales: SaleVM[] = [];
  allExitNotes: ExitNoteVM[] = [];

  sales: SaleVM[] = [];
  exitNotes: ExitNoteVM[] = [];

  filterSales = '';
  filterExitNotes = '';

  startDate = '';
  endDate = '';

  selectedExitNoteId: number | null = null;
  selectedSaleId: number | null = null; // 👈 solo una factura por nota de entrada

  buttonAssociate: ButtonLoading = new ButtonLoading('btn-success', false, 'GUARDAR', 'fa-save');

  paginationSales = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationExitNotes = new SimplePaginationModel(0, 5, 0, 0, 0);

  currentSales = [];
  currentAssociation: AssociationExitNoteSaleResponse | null = null;
  invoiceAssociation: AssociationExitNoteSaleResponse | null = null;
  currentExitNote: ExitNote | null = null;
  currentRelatedId: number | null = null;
  baseSales: SaleVM[] = [];
  loadingSaleDetails = false;

  @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef;
  // @HostListener('window:resize') onResize() { this.drawMultipleLinks(); }

  private timeoutSales: any = null;
  private timeoutNotes: any = null;

  constructor(
    private toastService: ToastrService,
    private associationExitNoteService: AssociationExitNoteService,
    private salesService: SalesService,
    private exitNoteService: ExitNoteService
  ) {
    const today = new Date();
    this.startDate = getStringDate(today);
    this.endDate = getStringDate(today);
  }

  ngOnInit(): void {
    this.loadStartData();

  }

  loadStartData() {
    this.loadExitNotes();
    this.loadClientInvoices();
  }

  private normalizeExitNote(en: ExitNote): ExitNoteVM {
    return {
      id: en.id,
      number: String(en.noteNumber),
      clientName: en.providerData?.name ?? '—',
      date: en.exitDate + "",
    };
  }

  private normalizeSale(association: any): SaleVM {
    const composedNumber = association.clientInvoiceData.series ? `${association.clientInvoiceData.series}-${association.clientInvoiceData.number}` : String(association.clientInvoiceData.number);
    return {
      id: association.clientInvoice,
      number: composedNumber,
      clientName: association.clientInvoiceData.clientData?.name ?? '—',
      date: association.clientInvoiceData.date ?? association.clientInvoiceData.accountingDate,
      total: association.clientInvoiceData.totalCost ?? 0,
      association: association
    };
  }

  loadClientInvoices() {
    this.associationExitNoteService.getAllAssociationExitNoteSale().subscribe({
      next: (response: ResponseObject) => {
        this.baseSales = (response.data || []).map((p: any) => this.normalizeSale(p));
        this.resetAllSalesToBase();
        this.paginationSales.page = 0;
        this.refreshSalesPagination();
        this.drawMultipleLinks();
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error("Error al obtener las ventas no asociadas", "Error");
      }
    });
  }

  /** Cargar notas de entrada */
  loadExitNotes() {
    this.exitNoteService.getExitNotesAll().subscribe({
      next: (resp: ResponseObject) => {
        this.exitNotes = (resp.data || []).map((en: any) => this.normalizeExitNote(en));
        this.allExitNotes = this.exitNotes;
        this.refreshExitNotesPagination();
      },
      error: () => this.toastService.error('Error al obtener notas de entrada')
    });
  }

  /** Seleccionar una ExitNote */
  selectExitNote(exitNoteId: number) {
    if (this.selectedExitNoteId === exitNoteId) return;

    this.selectedExitNoteId = exitNoteId;
    this.selectedSaleId = null;

    this.associationExitNoteService.getAssociationSaleByExitNoteId(exitNoteId).subscribe({
      next: (resp: ResponseObject) => {
        console.log(resp.data);
        
        if (resp.data.length > 0) {
          this.currentAssociation = resp.data[0];
          this.invoiceAssociation = resp.data[0];
          this.selectedSaleId = resp.data[0].clientInvoice || null;
          this.currentRelatedId = this.selectedSaleId;
        } else {
          this.currentAssociation = null;
          this.invoiceAssociation = null;
          this.selectedSaleId = null;
          this.currentRelatedId = this.selectedSaleId;
        }

        this.paginationSales.page = 0;
        this.refreshSalesPagination();
        this.getSaleDetails();
        this.drawMultipleLinks();
      },
      error: () => {
        this.currentAssociation = null;
        this.toastService.error('Error al obtener detalles de la nota de entrada');
      }
    });

    this.exitNoteService.getExitNoteById(exitNoteId).subscribe({
      next: (resp: ResponseObject) => {
        this.currentExitNote = resp.data;
      },
      error: () => {
        this.currentAssociation = null;
        this.toastService.error('Error al obtener detalles de la nota de entrada');
      }
    });
  }

  /** Seleccionar una factura (uno a uno) */
  toggleSale(p: SaleVM) {

    if (!this.selectedExitNoteId) return;

    if (this.isSaleLocked(p)) {
      this.toastService.warning('Esta venta ya está asociada a otra factura.');
      return;
    }

    const idx = this.selectedSaleId;
    if (idx != null) {
      if (idx === p.id) {
        this.selectedSaleId = null;
        this.currentRelatedId = null;
      } else {
        this.selectedSaleId = p.id;
        this.currentRelatedId = p.id;
      }
    } else {
      this.currentRelatedId = p.id;
      this.selectedSaleId = p.id;
    }
    this.invoiceAssociation = p.association;
    this.getSaleDetails();
    this.refreshSalesPagination();
    this.drawMultipleLinks();
  }

  /** Regla: una factura solo puede estar asociada a UNA exit note */
  isSaleLocked(p: SaleVM): boolean {
    // if (!p.associatedOrderId) return false;
    // // Está asociada a otra factura distinta a la seleccionada
    // return this.selectedSaleId != null && p.associatedOrderId !== this.selectedSaleId;
    return false;
  }

  getDisabledSave(): boolean {
    return !this.selectedExitNoteId || !this.selectedSaleId || this.buttonAssociate.loading;
  }

  /** Guardar la asociación */
  saveAssociation() {

    if (!this.selectedExitNoteId || !this.selectedSaleId || !this.invoiceAssociation) return;

    if (this.currentAssociation && this.selectedSaleId == this.currentAssociation.clientInvoice) {
      this.toastService.info('Esta es la asociacion actual de la nota de salida', 'Info');
      return;
    } else {
      this.buttonAssociate.loading = true;
      this.save();
    }


  }

  save() {
    if (!this.selectedExitNoteId || !this.selectedSaleId) {
      return;
    }
    this.associationExitNoteService.assignExitNoteToAssociation(this.invoiceAssociation.id,
      {
        exitNoteId: this.selectedExitNoteId,
        clientInvoiceId: this.selectedSaleId
      }).subscribe({
        next: () => {
          this.toastService.success('Asociación actualizada correctamente', 'Éxito');
          this.selectExitNote(this.selectedExitNoteId!);
          this.reloadSales();
          this.buttonAssociate.loading = false;
        },
        error: (e: HttpErrorResponse) => {
          this.toastService.error(e?.error?.message || 'Error al actualizar asociación', 'Error');
          this.buttonAssociate.loading = false;
        }
      });
  }

  /** Obtener detalles de la factura seleccionada */
  getSaleDetails() {
    this.currentSales = [];

    if (!this.selectedSaleId) {
      return;
    }

    this.loadingSaleDetails = true;

    this.salesService.getDetailInvoice({ id: this.selectedSaleId }).subscribe({
      next: (resp: ResponseObject) => {
        this.currentSales = resp.data || [];
        this.loadingSaleDetails = false;
      },
      error: () => {
        this.toastService.error('Error al obtener detalles de la factura')
        this.loadingSaleDetails = false;
      }

    });
  }

  /** 🔄 Paginación de órdenes */
  refreshExitNotesPagination() {
    const q = (this.filterExitNotes || '').toLowerCase();
    const filtered = this.allExitNotes.filter(en =>
      (en.number || '').toLowerCase().includes(q) ||
      (en.clientName || '').toLowerCase().includes(q)
    );

    this.paginationExitNotes.total = filtered.length;
    this.paginationExitNotes.start = this.paginationExitNotes.page * this.paginationExitNotes.size;
    const end = this.paginationExitNotes.start + this.paginationExitNotes.size;
    this.paginationExitNotes.end = Math.min(end, filtered.length);

    this.exitNotes = filtered.slice(this.paginationExitNotes.start, this.paginationExitNotes.end);
    this.drawMultipleLinks();
  }

  /** 🔄 Recargar órdenes */
  reloadSales() {
    const start = getLongDate(this.startDate);
    const end = getLongDate(this.endDate);

    this.salesService.getSalesByIntervalDate(start, end).subscribe({
      next: (resp: ResponseObject) => {
        this.baseSales = (resp.data || []).map((p: any) => this.normalizeSale(p));
        this.resetAllSalesToBase();

        // if (this.selectedOrderId && this.currentRelatedSaleIds?.length) {
        //   const cached = this.associatedSalesCache.get(this.selectedOrderId) || [];
        //   if (cached.length) this.upsertAssociatedIntoAllSales(cached, this.selectedOrderId);

        //   const relatedSet = new Set(this.currentRelatedSaleIds);
        //   this.allSales = this.allSales.map(p => ({
        //     ...p,
        //     associatedOrderId: relatedSet.has(p.id) ? this.selectedOrderId : p.associatedOrderId ?? null
        //   }));
        // }

        this.paginationSales.page = 0;
        this.refreshSalesPagination();
        this.drawMultipleLinks();
      },
      error: () => this.toastService.error('Error al obtener ventas')
    });
  }

  private resetAllSalesToBase() {
    this.allSales = this.baseSales.map(p => ({ ...p }));
  }

  refreshSalesPagination() {
    const q = (this.filterSales || '').toLowerCase();
    const matches = (p: SaleVM) =>
      (p.number || '').toLowerCase().includes(q) ||
      (p.clientName || '').toLowerCase().includes(q);

    const associatedSet = new Set();
    associatedSet.add(this.currentAssociation?.clientInvoice);

    const pinned = this.allSales.filter(p => associatedSet.has(p.id));

    const restRaw = this.allSales.filter(p => !associatedSet.has(p.id) && matches(p));

    const score = (x: SaleVM) => {
      if (x.id == this.selectedExitNoteId) return 1;
      return 2;
    };
    const rest = restRaw.sort((a, b) => score(a) - score(b));

    const final = [...pinned, ...rest];

    this.paginationSales.total = final.length;
    this.paginationSales.start = this.paginationSales.page * this.paginationSales.size;
    const end = this.paginationSales.start + this.paginationSales.size;
    this.paginationSales.end = Math.min(end, final.length);

    this.sales = final.slice(this.paginationSales.start, this.paginationSales.end);

    this.drawMultipleLinks();
  }

  debouncedFilterNotes() {
    clearTimeout(this.timeoutNotes);
    this.timeoutNotes = setTimeout(() => {
      this.paginationExitNotes.page = 0;
      this.refreshExitNotesPagination();
    }, 250);
  }

  debouncedFilterSales() {
    clearTimeout(this.timeoutSales);
    this.timeoutSales = setTimeout(() => {
      this.paginationSales.page = 0;
      this.refreshSalesPagination();
    }, 250);
  }

  /** Dibuja líneas desde la factura seleccionada hacia cada venta actualmente “seleccionada” */
  drawMultipleLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = '';

    if (!this.selectedSaleId || !this.selectedExitNoteId) return;
    
    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();
      const selectedRow = document.querySelector('#table-notes tr.selected') as HTMLElement;
      if (!selectedRow) return;

      const selectedRect = selectedRow.getBoundingClientRect();
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      if (!this.currentRelatedId) return;
      const pid = this.selectedSaleId;

      const relatedRow = document.querySelector(`#table-sales tr[data-id="${pid}"]`) as HTMLElement;
      if (!relatedRow) return;

      const rect = relatedRow.getBoundingClientRect();
      const x2 = rect.left - parentRect.left - 12;
      const y2 = rect.top + rect.height / 2 - parentRect.top;
      const xMiddle = x1 + ((x2 - x1) / 2);

      this.drawLine(svg, x1, xMiddle, y1, y1);
      this.drawLine(svg, xMiddle, x2, y2, y2);
      this.drawLine(svg, xMiddle, xMiddle, y1, y2);

    }, 50);
  }

  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#001f54');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }
}
